/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import Reflux from 'reflux';

import { qualifyUrl } from 'util/URLUtils';
import fetch from 'logic/rest/FetchProvider';
import ApiRoutes from 'routing/ApiRoutes';
import UserNotification from 'util/UserNotification';
import type { SearchesConfig } from 'components/search/SearchConfig';
import { singletonStore, singletonActions } from 'logic/singleton';

type ConfigurationsActionsType = {
  list: (configType: any) => Promise<unknown>;
  listSearchesClusterConfig: () => Promise<unknown>;
  listMessageProcessorsConfig: (configType: any) => Promise<unknown>;
  listEventsClusterConfig: () => Promise<unknown>;
  listIndexSetsDefaultsClusterConfig: () => Promise<unknown>;
  listAllowListConfig: (configType: any) => Promise<unknown>;
  listPermissionsConfig: (configType: string) => Promise<unknown>;
  listUserConfig: (configType: string) => Promise<unknown>;
  update: (configType: any, config: any) => Promise<void>;
  updateAllowlist: (configType: any, config: any) => Promise<void>;
  updateIndexSetDefaults: (configType: any, config: any) => Promise<void>;
  updateMessageProcessorsConfig: (configType: any, config: any) => Promise<void>;
};
export const ConfigurationsActions = singletonActions('core.Configuration', () =>
  Reflux.createActions<ConfigurationsActionsType>({
    list: { asyncResult: true },
    listSearchesClusterConfig: { asyncResult: true },
    listMessageProcessorsConfig: { asyncResult: true },
    listEventsClusterConfig: { asyncResult: true },
    listIndexSetsDefaultsClusterConfig: { asyncResult: true },
    listAllowListConfig: { asyncResult: true },
    listPermissionsConfig: { asyncResult: true },
    listUserConfig: { asyncResult: true },
    update: { asyncResult: true },
    updateAllowlist: { asyncResult: true },
    updateIndexSetDefaults: { asyncResult: true },
    updateMessageProcessorsConfig: { asyncResult: true },
  }),
);

const urlPrefix = ApiRoutes.ClusterConfigResource.config().url;
export type Url = {
  id: string;
  value: string;
  title: string;
  type: string;
};

export type AllowListConfig = {
  entries: Array<Url>;
  disabled: boolean;
};
export type PermissionsConfigType = {
  allow_sharing_with_everyone: boolean;
  allow_sharing_with_users: boolean;
};
export type UserConfigType = {
  enable_global_session_timeout: boolean;
  global_session_timeout_interval: string;
  restrict_access_token_to_admins: boolean;
  allow_access_token_for_external_user: boolean;
  default_ttl_for_new_tokens: string;
};
export type ConfigurationsStoreState = {
  configuration: Record<string, any>;
  searchesClusterConfig: SearchesConfig;
  eventsClusterConfig: {};
  indexSetsDefaultConfig: {};
};

export const ConfigurationsStore = singletonStore('core.Configuration', () =>
  Reflux.createStore<ConfigurationsStoreState>({
    listenables: [ConfigurationsActions],

    configuration: {},
    searchesClusterConfig: {},
    eventsClusterConfig: {},
    indexSetsDefaultConfig: {},
    getInitialState() {
      return this.getState();
    },

    getState() {
      return {
        configuration: this.configuration,
        searchesClusterConfig: this.searchesClusterConfig,
        eventsClusterConfig: this.eventsClusterConfig,
        indexSetsDefaultConfig: this.indexSetsDefaultConfig,
      };
    },

    propagateChanges() {
      this.trigger(this.getState());
    },

    _url(path) {
      return qualifyUrl(urlPrefix + path);
    },

    list(configType) {
      const promise = fetch('GET', this._url(`/${configType}`));

      promise.then((response) => {
        this.configuration = { ...this.configuration, [configType]: response };
        this.propagateChanges();

        return response;
      });

      ConfigurationsActions.list.promise(promise);
    },

    listSearchesClusterConfig() {
      const promise = fetch('GET', this._url('/org.graylog2.indexer.searches.SearchesClusterConfig')).then(
        (response) => {
          this.searchesClusterConfig = response;
          this.propagateChanges();

          return response;
        },
      );

      ConfigurationsActions.listSearchesClusterConfig.promise(promise);
    },

    listMessageProcessorsConfig(configType) {
      const promise = fetch('GET', qualifyUrl('/system/messageprocessors/config')).then((response) => {
        this.configuration = { ...this.configuration, [configType]: response };
        this.propagateChanges();

        return response;
      });

      ConfigurationsActions.listMessageProcessorsConfig.promise(promise);
    },

    listAllowListConfig(configType) {
      const promise = fetch('GET', qualifyUrl('/system/urlallowlist')).then((response) => {
        this.configuration = { ...this.configuration, [configType]: response };
        this.propagateChanges();

        return response;
      });

      ConfigurationsActions.listAllowListConfig.promise(promise);
    },

    listPermissionsConfig(configType) {
      const promise = fetch('GET', this._url(`/${configType}`)).then((response: PermissionsConfigType) => {
        this.configuration = {
          ...this.configuration,
          // default values bellow should be the same in backend.
          [configType]: response || {
            allow_sharing_with_everyone: true,
            allow_sharing_with_users: true,
          },
        };

        this.propagateChanges();

        return response;
      });

      ConfigurationsActions.listPermissionsConfig.promise(promise);
    },

    listUserConfig(configType) {
      const promise = fetch('GET', this._url(`/${configType}`)).then((response: UserConfigType) => {
        this.configuration = {
          ...this.configuration,
          // default values bellow should be the same in backend.
          [configType]: response || {
            enable_global_session_timeout: false,
            global_session_timeout_interval: 'PT1H',
          },
        };

        this.propagateChanges();

        return response;
      });

      ConfigurationsActions.listUserConfig.promise(promise);
    },

    listEventsClusterConfig() {
      const promise = fetch('GET', this._url('/org.graylog.events.configuration.EventsConfiguration')).then(
        (response) => {
          this.eventsClusterConfig = response;
          this.propagateChanges();

          return response;
        },
      );

      ConfigurationsActions.listEventsClusterConfig.promise(promise);
    },

    listIndexSetsDefaultsClusterConfig() {
      const promise = fetch('GET', this._url('/org.graylog2.configuration.IndexSetsDefaultConfiguration')).then(
        (response) => {
          this.indexSetsDefaultConfig = response;
          this.propagateChanges();

          return response;
        },
      );
      ConfigurationsActions.listIndexSetsDefaultsClusterConfig.promise(promise);
    },

    updateIndexSetDefaults(configType, config) {
      const promise = fetch('PUT', qualifyUrl('/system/indices/index_set_defaults'), config);

      promise.then(
        () => {
          this.configuration = { ...this.configuration, [configType]: config };
          this.propagateChanges();
          UserNotification.success('Index defaults configuration updated successfully');

          return config;
        },
        (error) => {
          UserNotification.error(error.additional.body.message, 'Index defaults configuration update failed');
        },
      );

      ConfigurationsActions.updateIndexSetDefaults.promise(promise);
    },

    update(configType, config) {
      const promise = fetch('PUT', this._url(`/${configType}`), config);

      promise.then(
        (response) => {
          this.configuration = { ...this.configuration, [configType]: response };
          this.propagateChanges();
          UserNotification.success('Configuration updated successfully');

          return response;
        },
        (error) => {
          UserNotification.error(
            `Search config update failed: ${error}`,
            `Could not update search config: ${configType}`,
          );
        },
      );

      ConfigurationsActions.update.promise(promise);
    },

    updateAllowlist(configType, config) {
      const promise = fetch('PUT', qualifyUrl('/system/urlallowlist'), config);

      promise.then(
        () => {
          this.configuration = { ...this.configuration, [configType]: config };
          this.propagateChanges();
          UserNotification.success('Url allowlist Configuration updated successfully');

          return config;
        },
        (error) => {
          UserNotification.error(
            `Url allowlist config update failed: ${error}`,
            `Could not update Url allowlist: ${configType}`,
          );
        },
      );

      ConfigurationsActions.updateAllowlist.promise(promise);
    },

    updateMessageProcessorsConfig(configType, config) {
      const promise = fetch('PUT', qualifyUrl('/system/messageprocessors/config'), config);

      promise.then(
        (response) => {
          this.configuration = { ...this.configuration, [configType]: response };
          this.propagateChanges();
          UserNotification.success('Configuration updated successfully');

          return response;
        },
        (error) => {
          UserNotification.error(
            `Message processors config update failed: ${error}`,
            `Could not update config: ${configType}`,
          );
        },
      );

      ConfigurationsActions.updateMessageProcessorsConfig.promise(promise);
    },
  }),
);
